import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HistoricVesselPoint } from '../../maritime-menu-options/history-panel/historic-vessel-point.model';
import { VesselHistoryData } from '../../models/vessels/maritime-ais-api';

interface MergedPointsFilters {
  minimumCount: number;
  minimumGapLengthHours: number;
  maximumDistanceMetres: number;
}

type IHistoryState = {
  disabled: boolean;
  selectedAISDataGapFeature: GeoJSON.Feature | null;
  selectedMergedPointIndex: number | null;
  historicVesselPoints: HistoricVesselPoint[];
  filteredVesselIds: string[];
  vesselHistoryData: VesselHistoryData | null;
  mergedPointsFilters: MergedPointsFilters;
};

const INITIAL_HISTORY_STATE: IHistoryState = {
  disabled: false,
  selectedAISDataGapFeature: null,
  selectedMergedPointIndex: null,
  historicVesselPoints: [],
  filteredVesselIds: [],
  vesselHistoryData: null,
  mergedPointsFilters: {
    minimumCount: 10,
    minimumGapLengthHours: 12,
    maximumDistanceMetres: 100,
  },
};

const HistorySlice = createSlice({
  name: 'history',
  initialState: INITIAL_HISTORY_STATE,
  reducers: {
    setSelectedAISDataGapFeature: (
      state,
      action: PayloadAction<GeoJSON.Feature | null>
    ) => {
      state.selectedAISDataGapFeature = action.payload;
    },
    setHistoricVesselPoints: (
      state,
      action: PayloadAction<HistoricVesselPoint[]>
    ) => {
      state.historicVesselPoints = action.payload;
    },
    prependHistoricVesselPoints: (
      state,
      action: PayloadAction<HistoricVesselPoint[]>
    ) => {
      state.historicVesselPoints = [
        ...action.payload,
        ...state.historicVesselPoints,
      ];
    },
    // this is a bit of a pain due to the data segment being
    // an array of vessels, which then contain messages.
    // redux requires immutability so we can't just array.push
    prependVesselHistoryData: (
      state,
      action: PayloadAction<VesselHistoryData>
    ) => {
      if (!state.vesselHistoryData) {
        state.vesselHistoryData = action.payload;
        return;
      }
      const newState: VesselHistoryData = {
        ...state.vesselHistoryData,
        data: [],
      };
      const existingVesselIds = state.vesselHistoryData.data.map(
        (v) => v.vessel.vessel_id
      );
      state.vesselHistoryData.data.forEach((vesselData) => {
        const newVesselData = action.payload.data.find(
          (newData) => newData.vessel.vessel_id === vesselData.vessel.vessel_id
        );
        if (newVesselData) {
          newState.data.push({
            ...vesselData,
            messages: [...newVesselData.messages, ...vesselData.messages],
          });
        } else {
          newState.data.push(vesselData);
        }
      });
      action.payload.data.forEach((newVesselData) => {
        if (!existingVesselIds.includes(newVesselData.vessel.vessel_id)) {
          newState.data.push(newVesselData);
        }
      });
      state.vesselHistoryData = newState;
    },
    clearVesselHistoryData: (state) => {
      state.selectedAISDataGapFeature = null;
      state.selectedMergedPointIndex = null;
      state.vesselHistoryData = null;
      state.historicVesselPoints = [];
    },
  },
});

export const {
  setSelectedAISDataGapFeature,
  setHistoricVesselPoints,
  prependHistoricVesselPoints,
  prependVesselHistoryData,
  clearVesselHistoryData,
} = HistorySlice.actions;

export default HistorySlice.reducer;
